import React, { useMemo } from 'react';
import { IconFolder } from '@tabler/icons-react';
import { MRT_ColumnDef, MRT_SortingState } from 'mantine-react-table';
import { Avatar } from 'src/design/atoms/Avatar';
import { Box } from 'src/design/atoms/Box';
import { EmptyState } from 'src/design/atoms/EmptyState';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { DropPrompt } from 'src/design/molecules/DropPrompt';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { DocumentDto } from 'src/api/FiretrolApiClient';
import { userDisplayName } from 'src/app/documents/documentAccess';
import { fileIcon } from 'src/app/documents/fileIcons';
import { formatDate, humanFileSize } from 'src/utils';
import { useAuth } from 'src/hooks/useAuth';
import classes from './FileTable.module.css';

export interface IFileTableProps {
  folders: DocumentDto[];
  documents: DocumentDto[];
  search: string;
  isLoading: boolean;
  selectedDocumentId: string | null;
  onOpenFolder: (folderId: string) => void;
  onSelectDocument: (document: DocumentDto) => void;
  onUploadClick?: () => void;
}

// Also the number of skeleton rows the table draws while loading.
const PAGE_SIZE = 6;

const ownerName = (row: DocumentDto, currentUserId: number): string =>
  userDisplayName(row.uploadedById, row.uploadedBy?.fullName, currentUserId);

const folderItemCount = (folder: DocumentDto): number =>
  (folder.documents?.length ?? 0) + (folder.children?.length ?? 0);

const sizeLabel = (row: DocumentDto): string => {
  if (!row.isFolder) return humanFileSize(row.size);
  const count = folderItemCount(row);
  return count === 1 ? '1 item' : `${count} items`;
};

const matchesSearch = (row: DocumentDto, search: string): boolean =>
  (row.name ?? '').toLowerCase().includes(search);

const sortValue = (
  row: DocumentDto,
  columnId: string,
  currentUserId: number,
): string | number => {
  switch (columnId) {
    case 'owner':
      return ownerName(row, currentUserId).toLowerCase();
    case 'modified':
      return row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
    case 'size':
      return row.isFolder ? folderItemCount(row) : row.size ?? 0;
    default:
      return (row.name ?? '').toLowerCase();
  }
};

const compareBy = (
  columnId: string,
  desc: boolean,
  currentUserId: number,
): ((a: DocumentDto, b: DocumentDto) => number) => {
  return (a, b) => {
    const left = sortValue(a, columnId, currentUserId);
    const right = sortValue(b, columnId, currentUserId);
    if (left === right) return 0;
    const order = left > right ? 1 : -1;
    return desc ? -order : order;
  };
};

const getColumns = (currentUserId: number): MRT_ColumnDef<DocumentDto>[] => [
  {
    id: 'name',
    accessorFn: (row) => row.name ?? '',
    header: 'Name',
    // MRT's grid layout pins each column's minimum width to its `size`, so this
    // is the floor the four columns need to fit a pane with the aside open.
    size: 180,
    grow: true,
    Cell: ({ row }) => (
      <Group gap="xs" wrap="nowrap" flex={1} miw={0}>
        {row.original.isFolder ? (
          <IconFolder size={18} className={classes.folderIcon} />
        ) : (
          fileIcon(row.original.type)
        )}
        <Text size="sm" c="bright" truncate miw={0}>
          {row.original.name}
        </Text>
      </Group>
    ),
  },
  {
    id: 'owner',
    accessorFn: (row) => (row.isFolder ? '' : ownerName(row, currentUserId)),
    header: 'Owner',
    size: 170,
    grow: false,
    Cell: ({ row }) => {
      if (row.original.isFolder) return null;
      const owner = ownerName(row.original, currentUserId);
      return (
        <Group gap="xs" wrap="nowrap" flex={1} miw={0}>
          <Avatar size="sm" radius="xl" color="initial" name={owner} />
          <Text size="sm" truncate miw={0}>
            {owner}
          </Text>
        </Group>
      );
    },
  },
  {
    id: 'modified',
    accessorFn: (row) => formatDate(row.updatedAt, 'MMM D, YYYY'),
    header: 'Modified',
    size: 130,
    grow: false,
  },
  {
    id: 'size',
    accessorFn: (row) => sizeLabel(row),
    header: 'Size',
    size: 96,
    grow: false,
    mantineTableHeadCellProps: { align: 'right' },
    mantineTableBodyCellProps: {
      style: { justifyContent: 'flex-end', textAlign: 'right' },
    },
    Cell: ({ row }) => (
      <Text size="sm" c={row.original.isFolder ? 'dimmed' : undefined}>
        {sizeLabel(row.original)}
      </Text>
    ),
  },
];

export const FileTable: React.FC<IFileTableProps> = ({
  folders,
  documents,
  search,
  isLoading,
  selectedDocumentId,
  onOpenFolder,
  onSelectDocument,
  onUploadClick,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.id ?? -1;

  const columns = useMemo(() => getColumns(currentUserId), [currentUserId]);

  const tableState = useTableState({
    identifier: 'job-documents-file-table',
    columns,
    defaultSorting: [{ id: 'name', desc: false }],
    defaultPagination: { pageIndex: 0, pageSize: PAGE_SIZE },
    defaultDensity: 'xs',
  });

  const sorting: MRT_SortingState = tableState.sorting;

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const sortedFolders = folders
      .filter((folder) => matchesSearch(folder, query))
      .sort(compareBy('name', false, currentUserId));
    const activeSort = sorting[0];
    const sortedFiles = documents
      .filter((doc) => !doc.isFolder && matchesSearch(doc, query))
      .sort(
        compareBy(
          activeSort?.id ?? 'name',
          activeSort?.desc ?? false,
          currentUserId,
        ),
      );
    return [...sortedFolders, ...sortedFiles];
  }, [folders, documents, search, sorting, currentUserId]);

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      columns,
      data: rows,
      enableRowActions: false,
      enableClickToCopy: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableColumnResizing: false,
      enableGlobalFilter: false,
      enablePagination: false,
      enableTopToolbar: false,
      enableBottomToolbar: false,
      // Flex layout so Name absorbs the leftover width instead of the table
      // overflowing its pane at narrow widths.
      layoutMode: 'grid',
      mantinePaperProps: { withBorder: false, radius: 0 },
      mantineTableProps: {
        highlightOnHover: true,
        withColumnBorders: false,
      },
      mantineTableBodyRowProps: ({ row }) => {
        // MRT renders the empty-rows fallback inside a synthetic row with no
        // original document, so it must not carry any row behaviour.
        if (!row.original.id) return {};
        const isSelected = row.original.id === selectedDocumentId;
        return {
          onClick: () =>
            row.original.isFolder
              ? onOpenFolder(row.original.id)
              : onSelectDocument(row.original),
          'aria-selected': isSelected,
          style: {
            cursor: 'pointer',
            ...(isSelected
              ? { boxShadow: 'inset 3px 0 0 var(--mantine-color-blue-6)' }
              : {}),
          },
        };
      },
      renderEmptyRowsFallback: () =>
        search ? (
          <Box p="xl">
            <EmptyState
              header="No matches"
              description={`No files or folders match "${search}"`}
            />
          </Box>
        ) : (
          <Box p="xl">
            <UnstyledButton
              className={classes.emptyDropTarget}
              onClick={onUploadClick}
            >
              <DropPrompt
                title="Drag files here or click to upload"
                description="This folder is empty"
              />
            </UnstyledButton>
          </Box>
        ),
    },
    getRowId: (row, index) => row?.id ?? String(index),
    totalCount: rows.length,
    isLoading,
    isFetching: false,
    isError: false,
    showResetStateButton: false,
  });

  return <>{table}</>;
};
